/* eslint-disable @typescript-eslint/no-base-to-string */
import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize", sans-serif;
        text-align: center;
        width: 100%;
        height: 100%;
        display: grid;
        grid-template-rows: auto 1fr auto;
        overflow: hidden;
    }

    /* Background and Header */
    .header {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: -2;
    }

    .header img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        object-fit: cover;
        background-position: center;
        background-repeat: no-repeat;
        z-index: -2;
    }

    .header .layer-image {
        position: absolute;
        width: 1000px;
        max-width: 1500px;
        height: auto;
        top: 10vh;
        left: 50%;
        transform: translateX(-50%);
        object-fit: cover;
        background-position: center;
        background-repeat: no-repeat;
    }

    /* Title */
    .title {
        font-size: 2.5rem;
        font-weight: bold;
        color: #E8D7B4;
        display: none;
    }

    /* Textbox */
    .textbox {
        position: absolute;
        bottom: 15%;
        left: 50%;
        transform: translateX(-50%);
        width: 85%;
        max-width: 900px;
        background-color: #5A3E1B;
        border: 2px solid #A68D60;
        padding: 25px;
        border-radius: 15px;
        text-align: left;
        display: flex;
        flex-direction: column;
        align-items: center;
        box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.4);
        transition: all 0.3s ease-in-out;
        max-height: 250px;
        overflow-y: auto;
    }


    .content {
        width: 100%;
        color: white;
        line-height: 1.6;
    }

        /* Footer */
    .footer {
        position: fixed;
        bottom: 5%;
        left: 50%;
        transform: translateX(-50%);
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: transparent;
        padding: 10px 0;
    }

    .footer .buttons {
        font-size: 16px;
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        justify-content: center;
    }

    .footer .button {
        background-color: #5A3E1B;
        border: 2px solid #A68D60;
        padding: 15px 25px;
        margin: 5px;
        text-transform: uppercase;
        border-radius: 10px;
        cursor: pointer;
        color: #E8D7B4;
        box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.5);
        transition: all 0.3s ease-in-out;
    }

    .footer .button:hover,
    .footer .button.active {
        background-color: #8B0000;
        border-color: #D4AF37;
        color: #FFF5D1;
        transform: scale(1.05);
    }


    .footer .game-object-button {
        background-color: #2F4F4F;
        border: 2px solid #8B5E3C;
        color: #E8D7B4;
        padding: 15px 25px;
        margin: 5px;
        border-radius: 10px;
        cursor: pointer;
        text-transform: uppercase;
        box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.5);
        transition: all 0.3s ease-in-out;
    }

    .footer .game-object-button:hover,
    .footer .game-object-button.active {
        background-color: #556B2F;
        border-color: #D4AF37;
        color: #FFF5D1;
        transform: scale(1.05);
    }

    /* Responsive */
    @media (max-width: 768px) {
        p {
            font-size: 16px;
        }

        .textbox {
            width: 95%;
            padding: 20px;
        }

        .content {
            font-size: 1.1rem;
        }

        .footer .button {
            padding: 12px 24px;
            font-size: 1rem;
        }
        
        .header .layer-image {
        width: 1000px;
        max-width: 800px;
        top: 30vh;
        }

        .footer {
            bottom: 10%;
            left: 50%;
            transform: translateX(-50%);
            width: 100%;
            height: auto;
            background-color: transparent;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 10px 0;
        }
    }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderTitle()}
            ${this.renderHeader()}
            ${this.renderContent()}
            ${this.renderFooter()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] = this._currentGameState?.roomImages ?? [];

        // Removed unnecessary parentheses around the argument inside the map function
        return roomImages.length > 0
            ? `<div class="header">
                ${roomImages.map(url =>
                    `<img src="/assets/img/rooms/${url}.png" class="${url.includes("layers") ? "layer-image" : ""}" />`
            ).join("")}
            </div>`
            : "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
        <div class="textbox">
            <div class="content">
                ${this._currentGameState?.text.map(text => `<p>${text}</p>`).join("") || ""}
            </div>
            <div class="buttons">
                ${this._currentGameState?.actions.map(button => this.renderActionButton(button)).join("")}
            </div>
        </div>
    `;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                    </div>
                    <div>
                        ${this._selectedActionButton
                            ? this._currentGameState?.objects.map(button =>
                                this.renderGameObjectButton(button)
                            ) || ""
                            : ""}
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedActionButton === button ? "active" : ""}"> ${button.name} </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button game-object-button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }
}
